import type {
    Campaign,
    CampaignUpdate,
} from "@campaign/domain/models/campaign.model";
import { CropTypes } from "@campaign/domain/models/crop-type.model";
import { CampaignUpdateUseCase } from "@campaign/domain/usecases/campaign-update.usecase";
import type { CampaignModalFormValuesValidated } from "@campaign/presentation/components/campaign/campaign-modal-form.view";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type { Station } from "@station/domain/models/station.model";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class CampaignUpdateModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    cropTypes: CropTypes = CropTypes.empty();

    @observable
    stations: Readonly<Station>[] = [];

    @observable
    campaign: Undefinable<Readonly<Campaign>> = undefined;

    constructor(
        @inject(CampaignUpdateUseCase)
        private readonly campaignUpdateUseCase: CampaignUpdateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async updateCampaign(
        values: CampaignModalFormValuesValidated,
    ): Promise<Nullable<Campaign>> {
        LoadLayoutStore.start();

        const campaignData: CampaignUpdate = {
            id: Number(values.id.value),
            name: values.name,
            cropTypeId: values.cropTypeId,
            startDate: this.getDate(values.dateRange[0]),
            endDate: this.getDate(values.dateRange[1]),
            stationIds: values.stationIds,
        };

        const campaignUpdated = await this.campaignUpdateUseCase.execute(
            values.id.value,
            campaignData,
        );

        if (campaignUpdated) {
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return campaignUpdated;
    }

    private getDate(date: DateTime | undefined): string {
        return date ? date.toFormat("dd/MM/yyyy") : "";
    }

    @action
    setCampaign(campaign: Undefinable<Readonly<Campaign>>): void {
        runInAction(() => {
            this.campaign = campaign;
        });
    }
}
